/**
 * ═══════════════════════════════════════════════════════════════════════
 * SyncEngine — Central Sync Orchestrator
 * ═══════════════════════════════════════════════════════════════════════
 *
 * Replaces component-driven fetch patterns with a centralized,
 * connection-aware, event-driven sync engine.
 *
 * Responsibilities:
 *   1. API Request Deduplication — prevents same endpoint being hit
 *      multiple times within a configurable window
 *   2. Visit-Scoped Metadata Cache — fetchOncePerVisit(visitId) guard
 *   3. AbortController Management — cancels in-flight requests on
 *      unmount or visit change
 *   4. Debug Instrumentation — logs duplicate API calls, detects loops
 *   5. Mount-tracking — prevents ghost calls after component unmount
 *
 * Architecture:
 *
 *   WebSocket Event → Intent → SyncEngine.fetch() → Cache Check
 *     → (cache hit)  → return cached data
 *     → (cache miss) → deduplicated API call → cache update → return
 *
 * NOT:
 *   UI Change → Render → Effect → Fetch → State → Render → Fetch Again
 *
 * ═══════════════════════════════════════════════════════════════════════
 */

const _debug = process.env.REACT_APP_DEBUG === 'true';
function _log(...args) { if (_debug) console.log('[SyncEngine]', ...args); }

// ─── Request dedup tracking ──────────────────────────────────────────
// Maps endpoint key → { promise, timestamp, abortController }
const _inflightRequests = new Map();

// ─── Visit-scoped metadata cache ─────────────────────────────────────
// Maps visitId → { data, fetchedAt, version }
const _metadataCache = new Map();

// ─── API call tracking for debug ─────────────────────────────────────
// Maps endpoint → [{ timestamp, source }] (ring buffer per endpoint)
const _apiCallLog = new Map();
const API_LOG_WINDOW_MS = 5000; // Track calls within 5s window
const API_LOG_MAX_ENTRIES = 20;
const DUPLICATE_THRESHOLD_MS = 3000; // Warn if same API called within 3s

// ─── Configuration ───────────────────────────────────────────────────
const DEDUP_WINDOW_MS = 2000;       // Don't re-fetch same endpoint within 2s
const METADATA_CACHE_TTL_MS = 30000; // Metadata cache valid for 30s
const METADATA_STALE_TTL_MS = 60000; // Serve stale data up to 60s while revalidating

class SyncEngine {
  constructor() {
    // Track mounted component instances for ghost-call prevention
    this._mountedComponents = new Set();
    this._componentCounter = 0;
  }

  // ═══════════════════════════════════════════════════════════════════
  // Component Lifecycle Tracking
  // ═══════════════════════════════════════════════════════════════════

  /**
   * Register a component mount. Returns a mount token.
   * Call unmount(token) in cleanup to prevent ghost API calls.
   *
   * @param {string} componentName - For debug logging
   * @returns {{ token: number, isMounted: () => boolean, unmount: () => void }}
   */
  registerMount(componentName = 'unknown') {
    const token = ++this._componentCounter;
    this._mountedComponents.add(token);
    _log(`Mount: ${componentName} (#${token})`);

    return {
      token,
      isMounted: () => this._mountedComponents.has(token),
      unmount: () => {
        this._mountedComponents.delete(token);
        _log(`Unmount: ${componentName} (#${token})`);
      },
    };
  }

  // ═══════════════════════════════════════════════════════════════════
  // Deduplicated Fetch
  // ═══════════════════════════════════════════════════════════════════

  /**
   * Execute a fetch with deduplication.
   *
   * If the same `key` was fetched within DEDUP_WINDOW_MS, returns the
   * in-flight or recently completed promise instead of making a new call.
   *
   * @param {string}   key      - Dedup key (e.g. "metadata:visit123")
   * @param {Function} fetcher  - async () => data
   * @param {Object}   [opts]
   * @param {number}   [opts.dedupWindowMs]  - Override dedup window
   * @param {string}   [opts.source]         - Caller name for debug
   * @param {Function} [opts.isMounted]      - () => boolean, skip if unmounted
   * @returns {Promise<any>}
   */
  async dedupFetch(key, fetcher, opts = {}) {
    const {
      dedupWindowMs = DEDUP_WINDOW_MS,
      source = 'unknown',
      isMounted = () => true,
    } = opts;

    // Ghost-call guard
    if (!isMounted()) {
      _log(`Ghost call blocked: ${key} from ${source}`);
      return null;
    }

    // Track for debug instrumentation
    this._trackApiCall(key, source);

    // Check if request is already in flight or recently completed
    const existing = _inflightRequests.get(key);
    if (existing) {
      const age = Date.now() - existing.timestamp;
      if (age < dedupWindowMs) {
        _log(`Dedup hit: ${key} (${age}ms old, from ${existing.source})`);
        return existing.promise;
      }
    }

    // Create AbortController for this request
    const abortController = new AbortController();

    // Build the fetch promise
    const promise = (async () => {
      try {
        const result = await fetcher(abortController.signal);
        return result;
      } catch (err) {
        if (err.name === 'AbortError') {
          _log(`Aborted: ${key}`);
          return null;
        }
        throw err;
      } finally {
        // Clean up after a delay (keeps entry for dedup window)
        setTimeout(() => {
          const current = _inflightRequests.get(key);
          if (current && current.promise === promise) {
            _inflightRequests.delete(key);
          }
        }, dedupWindowMs);
      }
    })();

    _inflightRequests.set(key, {
      promise,
      timestamp: Date.now(),
      abortController,
      source,
    });

    return promise;
  }

  /**
   * Abort all in-flight requests matching a key prefix.
   * Useful for visit-scoped cleanup when navigating away.
   *
   * @param {string} prefix - Key prefix to match (e.g. "metadata:visit123")
   */
  abortByPrefix(prefix) {
    for (const [key, entry] of _inflightRequests.entries()) {
      if (key.startsWith(prefix)) {
        entry.abortController.abort();
        _inflightRequests.delete(key);
        _log(`Aborted by prefix: ${key}`);
      }
    }
  }

  // ═══════════════════════════════════════════════════════════════════
  // Visit-Scoped Metadata Cache
  // ═══════════════════════════════════════════════════════════════════

  /**
   * Fetch metadata for a visit, with caching and deduplication.
   *
   * - Returns cached data if fresh (< METADATA_CACHE_TTL_MS)
   * - Returns stale data + revalidates in background if aging
   * - Makes a single API call if cache miss (deduplicated)
   *
   * @param {string}   visitId
   * @param {Function} apiFetcher - async (visitId, signal?) => data
   * @param {Object}   [opts]
   * @param {boolean}  [opts.force]    - Bypass cache
   * @param {string}   [opts.source]   - Caller name for debug
   * @param {Function} [opts.isMounted] - () => boolean
   * @returns {Promise<{ data: any, fromCache: boolean }>}
   */
  async fetchMetadata(visitId, apiFetcher, opts = {}) {
    const {
      force = false,
      source = 'unknown',
      isMounted = () => true,
    } = opts;

    if (!visitId) return { data: null, fromCache: false };

    const cacheKey = `metadata:${visitId}`;

    // Check cache (unless forced)
    if (!force) {
      const cached = _metadataCache.get(visitId);
      if (cached) {
        const age = Date.now() - cached.fetchedAt;

        // Fresh cache — return immediately
        if (age < METADATA_CACHE_TTL_MS) {
          _log(`Cache hit (fresh): ${cacheKey} (${Math.round(age / 1000)}s old)`);
          return { data: cached.data, fromCache: true };
        }

        // Stale but usable — return stale + revalidate in background
        if (age < METADATA_STALE_TTL_MS) {
          _log(`Cache hit (stale-while-revalidate): ${cacheKey}`);
          // Background revalidation
          this.dedupFetch(cacheKey, async (signal) => {
            const result = await apiFetcher(visitId, signal);
            if (isMounted()) {
              this._updateMetadataCache(visitId, result);
            }
            return result;
          }, { source: `${source}:revalidate`, isMounted });
          return { data: cached.data, fromCache: true };
        }
      }
    }

    // Cache miss or forced — fetch with dedup
    const data = await this.dedupFetch(cacheKey, async (signal) => {
      const result = await apiFetcher(visitId, signal);
      this._updateMetadataCache(visitId, result);
      return result;
    }, { source, isMounted });

    return { data, fromCache: false };
  }

  /**
   * Invalidate metadata cache for a specific visit.
   * Called when we know data has changed (after add/delete/WS event).
   *
   * @param {string} visitId
   */
  invalidateMetadata(visitId) {
    _metadataCache.delete(visitId);
    // Also drop the in-flight/recent request so the next fetch can't reuse a
    // pre-write response within the dedup window (bk-1745).
    _inflightRequests.delete(`metadata:${visitId}`);
    _log(`Cache invalidated: metadata:${visitId}`);
  }

  /**
   * Invalidate all metadata caches.
   */
  invalidateAllMetadata() {
    _metadataCache.clear();
    _log('All metadata caches invalidated');
  }

  /**
   * Get cached metadata without fetching.
   * @param {string} visitId
   * @returns {any|null}
   */
  getCachedMetadata(visitId) {
    const cached = _metadataCache.get(visitId);
    if (!cached) return null;
    const age = Date.now() - cached.fetchedAt;
    if (age > METADATA_STALE_TTL_MS) {
      _metadataCache.delete(visitId);
      return null;
    }
    return cached.data;
  }

  _updateMetadataCache(visitId, data) {
    _metadataCache.set(visitId, {
      data,
      fetchedAt: Date.now(),
      version: (_metadataCache.get(visitId)?.version || 0) + 1,
    });
  }

  // ═══════════════════════════════════════════════════════════════════
  // Debug Instrumentation
  // ═══════════════════════════════════════════════════════════════════

  /**
   * Track an API call for duplicate detection.
   * @param {string} key    - Endpoint key
   * @param {string} source - Caller name
   */
  _trackApiCall(key, source) {
    const now = Date.now();

    if (!_apiCallLog.has(key)) {
      _apiCallLog.set(key, []);
    }

    const log = _apiCallLog.get(key);

    // Check for duplicates within threshold
    const recentCalls = log.filter(
      (entry) => now - entry.timestamp < DUPLICATE_THRESHOLD_MS
    );

    if (recentCalls.length > 0) {
      console.warn(
        `[SyncEngine] ⚠️ Duplicate API call detected: "${key}"`,
        `\n  Called ${recentCalls.length + 1}x within ${DUPLICATE_THRESHOLD_MS}ms`,
        `\n  Previous: ${recentCalls.map((e) => e.source).join(', ')}`,
        `\n  Current: ${source}`,
        `\n  This may indicate a re-render loop or missing deduplication.`
      );
    }

    // Track this call
    log.push({ timestamp: now, source });

    // Trim old entries
    const cutoff = now - API_LOG_WINDOW_MS;
    while (log.length > 0 && log[0].timestamp < cutoff) {
      log.shift();
    }
    if (log.length > API_LOG_MAX_ENTRIES) {
      log.splice(0, log.length - API_LOG_MAX_ENTRIES);
    }
  }

  /**
   * Get diagnostic info for debugging.
   * Call window.__akSyncDiag() in browser console.
   */
  getDiagnostics() {
    const inflight = {};
    _inflightRequests.forEach((entry, key) => {
      inflight[key] = {
        age: `${Math.round((Date.now() - entry.timestamp) / 1000)}s`,
        source: entry.source,
      };
    });

    const cache = {};
    _metadataCache.forEach((entry, visitId) => {
      cache[visitId] = {
        age: `${Math.round((Date.now() - entry.fetchedAt) / 1000)}s`,
        version: entry.version,
        entriesCount: entry.data?.entries?.length ?? '?',
      };
    });

    const apiCalls = {};
    _apiCallLog.forEach((log, key) => {
      apiCalls[key] = {
        recentCount: log.length,
        sources: [...new Set(log.map((e) => e.source))],
      };
    });

    return {
      inflightRequests: inflight,
      metadataCache: cache,
      apiCallLog: apiCalls,
      mountedComponents: this._mountedComponents.size,
    };
  }

  /**
   * Reset all state — for testing or hot-reload cleanup.
   */
  reset() {
    // Abort all in-flight
    _inflightRequests.forEach((entry) => entry.abortController.abort());
    _inflightRequests.clear();
    _metadataCache.clear();
    _apiCallLog.clear();
    this._mountedComponents.clear();
    this._componentCounter = 0;
    _log('SyncEngine reset');
  }
}

// ── Singleton ────────────────────────────────────────────────────────
const syncEngine = new SyncEngine();

// ── Global diagnostic ────────────────────────────────────────────────
if (typeof window !== 'undefined') {
  window.__akSyncDiag = () => syncEngine.getDiagnostics();
}

export default syncEngine;
